import { isNotNullish } from '@adornis/assert/isNullish.js';
import { finalizeAuthenticationClientSide } from '@adornis/authentication/client/finalize-authentication.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet, type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { useLegacyForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { html, nothing } from 'lit';
import { PublicFooter } from '../../../client/public-footer.js';
import { fileStorage } from '../../../client/utils/indexeddb-file-storage.js';
import { GroupsAPI } from '../../../groups/api/GroupsAPI.js';
import { getAbstractSchoolType } from '../../../groups/api/getAbstractSchoolType.js';
import { getPossibleKlassenForSchoolType, klasseLabel } from '../../../groups/api/getPossibleKlassenForSchoolType.js';
import { Person } from '../../../users/entities/person.js';
import type { InterviewSlot } from '../../interviews/entities/InterviewSlot.js';
import { calculateRegisterMode, RegisterMode } from '../../registrationMode.js';
import { getSuSRegistrationDataForStudent } from '../api/getSuSRegistrationDataForStudent.js';
import { loginWithSuSUpdateToken } from '../api/loginWithSuSUpdateToken.js';
import {
  RegisterStudentData,
  RegisterStudentDataFiles,
  registerStudentWithSuSForm,
} from '../api/registerStudentWithSuSForm.js';
import { readRegistrationDraft, registrationDraftKey } from '../registration-draft.js';
import {
  pickClearsError,
  readRegistrationSection,
  registrationSectionKey,
  sectionForField,
  sectionsForMode,
  type RegistrationSectionID,
} from '../registration-sections.js';
import { SusEntryFormDataSheet } from '../sus-entry-form-data-sheet.js';
import { RegistrationField } from './fields/registration-field.js';
import { RegistrationInfoScreen } from './registration-info-screen.js';
import { needsAddressConfirmation } from './sg-sus-entry-form-address-fields.js';
import { SgSusEntryFormFiles } from './sg-sus-entry-form-files.js';
import { SGSusEntryFormInterviewSlots } from './sg-sus-entry-form-interview-slots.js';
import { SgSusEntryFormSbFields } from './sg-sus-entry-form-sb-fields.js';
import { SgSusEntryFormSchoolData } from './sg-sus-entry-form-schooldata.js';
import { SgSusEntryFormSchoolingInterests } from './sg-sus-entry-form-schooling-interests.js';
import { SgSusEntryFormStudentFields } from './sg-sus-entry-form-student-fields.js';

/**
 * Everything the form takes over from an existing person. Loaded explicitly: the globally subscribed current user only
 * carries first and last name, and reading any other field of a network entity throws.
 */
const personPrefillSelection = {
  _id: 1,
  email: 1,
  profilDaten: {
    stammdaten: {
      vorname: 1,
      nachname: 1,
      geschlecht: 1,
      geburtsDatum: 1,
      geburtsLand: 1,
      geburtsOrt: 1,
      staatsangehoerigkeit: 1,
      staatsangehoerigkeit2: 1,
      religion: 1,
      titel: 1,
      telefonNr: 1,
    },
    adresse: { streetAndNumber: 1, zip: 1, city: 1, district: 1 },
  },
} as const satisfies BaseQLSelectionSet<Person>;
type PrefillPerson = NarrowedEntity<Person, typeof personPrefillSelection>;

/** An update link also brings back the answers of the application, the submit overwrites all of them. */
const childPrefillSelection = {
  ...personPrefillSelection,
  schuelerDaten: {
    hasSiblingsAtMartinusSchools: 1,
    potentialMartinusElementarySchoolID: 1,
    otherSchool: 1,
    lastGradeGerman: 1,
    lastGradeMath: 1,
    lastGradeSachunterricht: 1,
    interestInNativeLanguageCourses: 1,
    interestInWholeDaySchooling: 1,
    schoolyearsAfterElementary: 1,
    languageAtHomeIsGerman: 1,
    motivation: 1,
    schulwechselMotivation: 1,
    currentKlassenstufeAtBewerbungsDate: 1,
    primarySchoolTranscripts: 1,
    learningDisabilitiesFileID: 1,
    sorgerechtsurteilFileID: 1,
  },
} as const satisfies BaseQLSelectionSet<Person>;

// Disabled complexity here as its is just advanced null coalescing
// eslint-disable-next-line complexity
function setFirstSbFromUser(user: PrefillPerson, registerData: RegisterStudentData) {
  const sb = registerData.sorgeberechtigte[0];
  if (!sb) return;
  const stammdaten = user.profilDaten?.stammdaten;
  const adresse = user.profilDaten?.adresse;

  sb.potentialExistingSbID = user._id;
  sb.email = user.email;
  if (stammdaten?.vorname) sb.firstNameSB = stammdaten.vorname;
  if (stammdaten?.nachname) sb.lastNameSB = stammdaten.nachname;
  if (stammdaten?.geschlecht) sb.geschlecht = stammdaten.geschlecht;
  if (stammdaten?.titel) sb.title = stammdaten.titel;
  if (stammdaten?.religion) sb.religion = stammdaten.religion;
  if (stammdaten?.telefonNr) sb.telefonNr = stammdaten.telefonNr;
  if (adresse?.streetAndNumber) sb.streetAndNumber = adresse.streetAndNumber;
  if (adresse?.zip) sb.zipSB = adresse.zip;
  if (adresse?.city) sb.citySB = adresse.city;
  if (adresse?.district) sb.districtSB = adresse.district;

  return registerData;
}

// Disabled complexity here as its is just advanced null coalescing
// eslint-disable-next-line complexity
function setChildFromUser(
  user: NarrowedEntity<Person, typeof childPrefillSelection>,
  registerData: RegisterStudentData,
  update: boolean,
) {
  const stammdaten = user.profilDaten?.stammdaten;
  const adresse = user.profilDaten?.adresse;
  const schuelerDaten = user.schuelerDaten;

  registerData.potentialExistingChildID = user._id;
  if (stammdaten?.vorname) registerData.firstName = stammdaten.vorname;
  if (stammdaten?.nachname) registerData.lastName = stammdaten.nachname;
  if (stammdaten?.geschlecht) registerData.geschlecht = stammdaten.geschlecht;
  if (stammdaten?.geburtsDatum) registerData.birthday = stammdaten.geburtsDatum;
  if (stammdaten?.geburtsLand) registerData.countryOfBirth = stammdaten.geburtsLand;
  if (stammdaten?.geburtsOrt) registerData.placeofBirth = stammdaten.geburtsOrt;
  if (stammdaten?.staatsangehoerigkeit) registerData.staatsangehoerigkeit = stammdaten.staatsangehoerigkeit;
  if (stammdaten?.staatsangehoerigkeit2) registerData.staatsangehoerigkeit2 = stammdaten.staatsangehoerigkeit2;
  if (stammdaten?.religion) registerData.religion = stammdaten.religion;
  if (adresse?.streetAndNumber) registerData.streetAndNumber = adresse.streetAndNumber;
  if (adresse?.zip) registerData.zip = adresse.zip;
  if (adresse?.city) registerData.city = adresse.city;
  if (adresse?.district) registerData.district = adresse.district;

  // a new application of a known child starts fresh, its answers belonged to another application
  if (!update) return registerData;
  if (isNotNullish(schuelerDaten?.hasSiblingsAtMartinusSchools))
    registerData.hasSiblingsAtMartinusSchools = schuelerDaten.hasSiblingsAtMartinusSchools;
  if (schuelerDaten?.potentialMartinusElementarySchoolID)
    registerData.potentialMartinusElementarySchoolID = schuelerDaten.potentialMartinusElementarySchoolID;
  if (schuelerDaten?.otherSchool) registerData.potentialMartinusElementarySchoolName = schuelerDaten.otherSchool;
  if (isNotNullish(schuelerDaten?.lastGradeGerman)) registerData.lastGradeGerman = schuelerDaten.lastGradeGerman;
  if (isNotNullish(schuelerDaten?.lastGradeMath)) registerData.lastGradeMath = schuelerDaten.lastGradeMath;
  if (isNotNullish(schuelerDaten?.lastGradeSachunterricht))
    registerData.lastGradeSachunterricht = schuelerDaten.lastGradeSachunterricht;
  registerData.interestInNativeLanguageCourses = schuelerDaten?.interestInNativeLanguageCourses;
  registerData.interestInWholeDaySchooling = schuelerDaten?.interestInWholeDaySchooling;
  if (isNotNullish(schuelerDaten?.schoolyearsAfterElementary))
    registerData.schoolyearsAfterElementary = schuelerDaten.schoolyearsAfterElementary;
  if (isNotNullish(schuelerDaten?.languageAtHomeIsGerman))
    registerData.familyLanguageIsGerman = schuelerDaten.languageAtHomeIsGerman;
  if (schuelerDaten?.motivation) registerData.motivation = schuelerDaten.motivation;
  if (schuelerDaten?.schulwechselMotivation) registerData.schulwechselMotivation = schuelerDaten.schulwechselMotivation;
  if (schuelerDaten?.currentKlassenstufeAtBewerbungsDate)
    registerData.currentKlassenstufe = schuelerDaten.currentKlassenstufeAtBewerbungsDate;

  return registerData;
}

/**
 * Puts the documents of the saved application into the form's file storage, so the file inputs show them like
 * picked files. The submit hands their server ids back instead of uploading copies, an emptied input removes one.
 */
async function loadSavedDocuments(
  schuelerDaten: NarrowedEntity<Person, typeof childPrefillSelection>['schuelerDaten'],
): Promise<RegisterStudentDataFiles> {
  const load = (fileID: Maybe<string>) => (fileID ? fileStorage.storeServerFile(fileID) : Promise.resolve(null));
  const [learningDisabilities, sorgerechtsurteil, ...transcripts] = await Promise.all([
    load(schuelerDaten?.learningDisabilitiesFileID),
    load(schuelerDaten?.sorgerechtsurteilFileID),
    ...(schuelerDaten?.primarySchoolTranscripts ?? []).map(load),
  ]);
  const primarySchoolTranscripts = transcripts.filter(isNotNullish);
  return new RegisterStudentDataFiles({
    learningDisabilities,
    sorgerechtsurteil,
    // the form always offers one transcript input
    primarySchoolTranscripts: primarySchoolTranscripts.length ? primarySchoolTranscripts : [''],
  });
}

const pathOf = (error: ValidationError) => [...(error.extensions.path ?? []), error.extensions.key].join('.');
const interviewMissingMessage =
  'Bitte wählen Sie einen Termin für das Aufnahmegespräch. Falls keine Termine verfügbar sind, können Sie Ihren Entwurf später über den E-Mail-Link fortsetzen.';

export const SgSusEntryFormRegistrationForm = stateful((props: { token: Maybe<string> }) => {
  // get the data from the token
  const {
    value: userData,
    loading: userDataLoading,
    error: userDataError,
  } = useQuery(
    () =>
      getSuSRegistrationDataForStudent(props.token)({
        email: 1,
        schoolID: 1,
        userID: 1,
        childID: 1,
        klasse: 1,
        origin: 1,
        update: 1,
      }),
    [props.token],
  );

  /**
   * An update link logs its guardian in: the child's saved application is only readable for its guardians, and
   * the guardian edits the form as themselves. Everything loading persons waits until that is done.
   */
  const user = useObservable(() => currentUser<Person>(), []);
  const needsLogin = !!userData?.update && user?._id !== userData.userID;
  const [loginError, setLoginError] = useState('');
  useEffect(() => {
    if (!needsLogin) return;
    loginWithSuSUpdateToken(props.token ?? '')()
      .then(userID => finalizeAuthenticationClientSide({ userID, userClass: Person._class }))
      .catch((error: unknown) =>
        setLoginError(error instanceof Error ? error.message : 'Die Anmeldung über den Link ist fehlgeschlagen.'),
      );
  }, [needsLogin]);

  // get the school data
  const {
    value: schoolData,
    loading: schoolDataLoading,
    error: schoolDataError,
  } = useQuery(
    () =>
      GroupsAPI.getPublicSchools(
        false,
        userData?.schoolID,
      )({
        _id: 1,
        name: 1,
        imageID: 1,
        image: selectionSet(() => ImageFile, 2),
        schoolType: 1,
        mode: 1,
      }),
    [userData?.schoolID],
  );

  const {
    value: allSchools,
    loading: allSchoolsLoading,
    error: allSchoolsError,
  } = useQuery(
    () =>
      GroupsAPI.getPublicSchools(true)({
        name: 1,
        _id: 1,
        schoolType: 1,
      }),
    [],
  );
  const {
    value: childData,
    loading: childDataLoading,
    error: childDataError,
  } = useQuery(
    () =>
      userData?.childID && !needsLogin
        ? getByID(Person, userData.childID)(childPrefillSelection)
        : Promise.resolve(null),
    [userData?.childID, needsLogin],
  );

  // a logged in user is the first sorgeberechtigte
  const {
    value: guardianData,
    loading: guardianDataLoading,
    error: guardianDataError,
  } = useQuery(
    () => (user?._id ? getByID(Person, user._id)(personPrefillSelection) : Promise.resolve(null)),
    [user?._id],
  );

  const abstractSchoolType = getAbstractSchoolType(schoolData?.[0]?.schoolType);

  const token = props.token ?? '';
  const [registerData, updateRegisterData] = useState(
    new RegisterStudentData(readRegistrationDraft(localStorage, token) ?? {}),
    [token],
  );
  // the class can be changed in the form, the link's class only starts it
  const klasse = registerData.klassenstufe || userData?.klasse || '';
  const mode = calculateRegisterMode(klasse, userData?.origin);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [saveFailed, setSaveFailed] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [savedSection, setSavedSection] = useState(readRegistrationSection(localStorage, token), [token]);
  // taken over from the slot picker: a second subscription of the same query can lag behind the picker's live data
  const [selectedSlot, setSelectedSlot] = useState<Maybe<InterviewSlot>>(null);
  const sections = sectionsForMode(mode, !!userData?.update);
  const activeSection = sections.find(section => section.id === savedSection) ?? sections[0]!;
  const activeIndex = sections.indexOf(activeSection);

  // "Automatisch gespeichert" is shown briefly after every persisted input
  useEffect(() => {
    if (!savedAt) return;
    const timer = setTimeout(() => setSavedAt(0), 2500);
    return () => clearTimeout(timer);
  }, [savedAt]);

  const setRegisterData = (data: RegisterStudentData) => {
    try {
      localStorage.setItem(registrationDraftKey(token), JSON.stringify(data));
    } catch {
      setSaveFailed(true);
    }
    updateRegisterData(data);
  };

  const goToSection = (section: RegistrationSectionID) => {
    // Existing Engine inputs commit on blur. Commit before changing the visible screen.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setSavedSection(section);
    try {
      localStorage.setItem(registrationSectionKey(token), section);
    } catch {
      // the draft itself is what matters, and its failure is reported by setRegisterData
    }
    requestAnimationFrame(() => {
      document.getElementById('sus-registration-page')?.scrollTo({ top: 0 });
      document.getElementById('sus-section-heading')?.focus({ preventScroll: true });
    });
  };
  const showFieldError = (key: string, message: string) => {
    form.errors.value = [new ValidationError(message, { key })];
    setSubmitError(message);
    goToSection(sectionForField(key));
  };

  // if the user is logged in, we can prefill the form with the data of the user as the first sb
  useEffect(() => {
    // only a guardian the user already typed is kept - the child's prefill may have written the draft first
    const draftGuardians = readRegistrationDraft(localStorage, token)?.sorgeberechtigte;
    if (
      !guardianData ||
      guardianData._id === userData?.childID ||
      (Array.isArray(draftGuardians) && draftGuardians[0]?.firstNameSB)
    )
      return;
    const prefillData = setFirstSbFromUser(guardianData, registerData);
    if (prefillData) setRegisterData(prefillData);
  }, [guardianData]);

  // we also want to prefill the child data if the user has selected an existing child to be registered
  const [documentsLoading, setDocumentsLoading] = useState(false);
  useEffect(() => {
    if (!childData || registerData.firstName) return;
    if (!userData?.update) {
      setRegisterData(setChildFromUser(childData, registerData, false));
      return;
    }
    // the form stays behind the loading screen until the documents are in, edits made earlier would be overwritten
    setDocumentsLoading(true);
    loadSavedDocuments(childData.schuelerDaten)
      .then(files => {
        const prefilled = setChildFromUser(childData, registerData, true);
        prefilled.files = files;
        setRegisterData(prefilled);
      })
      .finally(() => setDocumentsLoading(false));
  }, [childData]);

  useEffect(() => {
    registerData.mode = mode;
    setRegisterData(registerData);
  }, [mode]);

  // Infuse the data of the sorgeberechtigte with the data of the user
  useEffect(() => {
    // if no schoolID is available, we can't do anything, as the form is school specific
    if (!userData?.schoolID) return;

    // there will always be at least one sorgeberechtigte. The creation of the userdata ensures this
    const firstSorgeberechtigte = registerData.sorgeberechtigte[0];
    if (!firstSorgeberechtigte) return;

    // infuse the data of the sorgeberechtigte with potential already existing data - unless the link went to the
    // applicant, who then enters their guardians
    if (userData.userID !== userData.childID) {
      firstSorgeberechtigte.potentialExistingSbID = userData.userID;
      firstSorgeberechtigte.email = userData.email;
    }

    registerData.token = props.token ?? '';
    registerData.schoolID = userData.schoolID;
    registerData.klassenstufe ||= userData.klasse;
    registerData.update = !!userData.update;
    setRegisterData(registerData);
  }, [userData]);

  const form = useLegacyForm(
    constructValue(registerData),
    {
      onSuccess: (e: RegisterStudentData) => {
        // check if the user tried to register multiple sb with the same email
        const emails = e.sorgeberechtigte.map(sb => sb.email);
        if (emails.length !== new Set(emails).size) {
          throw new ValidationError(
            'Bitte verwenden Sie unterschiedliche E-Mail-Adressen, oder lassen Sie den zweiten Sorgeberechtigten weg.',
            { key: 'email', path: ['sorgeberechtigte', 1] },
          );
        }
        // an address without a recognized house number has to be confirmed (see needsAddressConfirmation)
        const unconfirmed = [
          { line: e.streetAndNumber, path: [] },
          ...e.sorgeberechtigte.map((sb, index) => ({ line: sb.streetAndNumber, path: ['sorgeberechtigte', index] })),
        ].find(({ line }) => needsAddressConfirmation(line, e.confirmedAddresses));
        if (unconfirmed) {
          throw new ValidationError('Bitte ergänzen Sie die Hausnummer oder bestätigen Sie die Adresse.', {
            key: 'streetAndNumber',
            path: unconfirmed.path,
          });
        }
      },
      success: async (e: RegisterStudentData) => {
        setBusy(true);
        setSubmitError('');
        try {
          const files = await Promise.all(
            e.files.primarySchoolTranscripts.filter(Boolean).map(fileID => fileStorage.getFile(fileID)),
          );
          const learningDisabilities = await fileStorage.getFile(e.files.learningDisabilities ?? '');
          const sorgerechtsurteil = await fileStorage.getFile(e.files.sorgerechtsurteil ?? '');
          if (
            files.some(file => !file) ||
            (e.files.learningDisabilities && !learningDisabilities) ||
            (e.files.sorgerechtsurteil && !sorgerechtsurteil)
          ) {
            showFieldError(
              'files',
              'Eine gespeicherte Datei ist auf diesem Gerät nicht mehr vorhanden. Bitte wählen Sie die Datei erneut aus.',
            );
            return;
          }
          const confirmed = await showDialog<boolean>({
            modalBoxClasses: 'w-full max-w-3xl max-h-[calc(100dvh-1rem)]',
            content: ok =>
              html`<div class="flex min-w-0 flex-col gap-5 p-4 sm:p-6">
                <h2 class="font-semibold md:text-lg">${e.update ? 'Aktualisierung prüfen' : 'Bewerbung prüfen'}</h2>
                <p>
                  Bitte prüfen Sie alle Angaben. Mit „${e.update ? 'Aktualisierung absenden' : 'Bewerbung absenden'}“
                  übermitteln Sie die Daten an die Schule. Sie erhalten anschließend eine Bestätigung per E-Mail.
                </p>
                ${SusEntryFormDataSheet(e, schoolData?.[0]?.name ?? '', allSchools?.find(s => s._id === e.potentialMartinusElementarySchoolID)?.name ?? 'Andere Schule', mode, selectedSlot, files.filter(isNotNullish), learningDisabilities, sorgerechtsurteil, true)}
                <div class="sticky bottom-0 flex flex-col gap-3 bg-base-100 py-3 sm:flex-row sm:justify-end">
                  <button type="button" class="btn btn-outline min-h-11" @click=${() => ok(false)}>
                    Weiter bearbeiten</button
                  ><button type="button" class="btn btn-primary min-h-11" @click=${() => ok(true)}>
                    ${e.update ? 'Aktualisierung absenden' : 'Bewerbung absenden'}
                  </button>
                </div>
              </div>`,
          }).catch(() => false);
          if (!confirmed) return;
          // Upload into a copy so retries retain the device's file IDs and the saved draft.
          const submission = constructValue<RegisterStudentData>(JSON.parse(JSON.stringify(e)));
          submission.files.primarySchoolTranscripts = await Promise.all(
            e.files.primarySchoolTranscripts.filter(Boolean).map(id => fileStorage.uploadFileToServer(id)),
          );
          if (e.files.sorgerechtsurteil)
            submission.files.sorgerechtsurteil = await fileStorage.uploadFileToServer(e.files.sorgerechtsurteil);
          if (e.files.learningDisabilities)
            submission.files.learningDisabilities = await fileStorage.uploadFileToServer(e.files.learningDisabilities);
          try {
            await registerStudentWithSuSForm(submission)();
          } catch (error) {
            // The transport re-sends an in-flight mutation after a reconnect, and that copy is throttled (or finds the
            // invitation used up) although the first run went through. The invitation is consumed only at the very end
            // of a successful registration, so a consumed invitation means the application was accepted.
            const invitationOpen = await getSuSRegistrationDataForStudent(token)({ schoolID: 1 }).then(
              () => true,
              e => !(e instanceof ExpectedError),
            );
            if (invitationOpen) throw error;
          }
          try {
            localStorage.removeItem(registrationDraftKey(token));
            localStorage.removeItem(registrationSectionKey(token));
            if (readRegistrationDraft(localStorage, token)) localStorage.removeItem('sus-register-data');
          } catch {
            // A browser storage failure must not turn an accepted application into a retry.
          }
          await showDialog<null>({
            modalBoxClasses: 'w-full max-w-xl max-h-[calc(100dvh-1rem)]',
            content: ok =>
              html`<div class="flex flex-col gap-4 p-4 sm:p-6">
                <span
                  class="icon-[material-symbols--check-circle-rounded] text-4xl text-primary"
                  aria-hidden="true"
                ></span>
                <h2 class="font-semibold md:text-lg">
                  ${e.update ? 'Aktualisierung erfolgreich versandt' : 'Bewerbung erfolgreich versandt'}
                </h2>
                <p>Sie erhalten eine Bestätigung per E-Mail. Bitte prüfen Sie auch Ihren Spamordner.</p>
                <button type="button" class="btn btn-primary" @click=${() => ok(null)}>Zur Schulauswahl</button>
              </div>`,
          }).catch(() => null);
          goTo('/sus-schulen');
        } catch (error) {
          setSubmitError(
            error instanceof Error
              ? error.message
              : 'Die Bewerbung konnte nicht versendet werden. Ihre Eingaben bleiben erhalten.',
          );
        } finally {
          setBusy(false);
        }
      },
      error: () => {
        setBusy(false);
        const first = sections.find(section =>
          form.errors.value.some(error => sectionForField(pathOf(error)) === section.id),
        );
        setSubmitError(
          first?.id === 'sus-interview'
            ? interviewMissingMessage
            : 'Bitte überprüfen Sie die markierten Pflichtfelder. Ihre Eingaben bleiben erhalten.',
        );
        if (first) goToSection(first.id);
      },
      onValuePicked: path => {
        if (!form.document) return;
        form.errors.value = form.errors.value.filter(error => !pickClearsError(path.join('.'), pathOf(error)));
        setRegisterData(form.document);
        setSavedAt(Date.now());
      },
    },
    [registerData, allSchools, schoolData, childData, guardianData, mode, selectedSlot],
  );
  const errorSections = new Set(form.errors.value.map(error => sectionForField(pathOf(error))));

  if (
    userDataLoading ||
    schoolDataLoading ||
    allSchoolsLoading ||
    childDataLoading ||
    guardianDataLoading ||
    documentsLoading ||
    (needsLogin && !loginError)
  )
    return html`<div class="flex min-h-0 flex-1 items-center justify-center gap-3 p-6" role="status">
      <span class="loading loading-spinner" aria-hidden="true"></span>Bewerbung wird geladen …
    </div>`;
  if (
    userDataError ||
    !userData?.schoolID ||
    !schoolData?.[0] ||
    schoolDataError ||
    allSchoolsError ||
    childDataError ||
    guardianDataError ||
    loginError
  )
    return html`<div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <main class="mx-auto w-full max-w-2xl flex-1 p-4 sm:p-6">
        <section class="card">
          <div class="card-body gap-4">
            <h1 class="text-2xl font-semibold">Bewerbung kann nicht geladen werden</h1>
            <p>
              ${loginError || (userDataError || !userData?.schoolID ? 'Der Link ist ungültig oder abgelaufen. Ein Bewerbungslink ist 21 Tage gültig. Bitte starten Sie die Bewerbung erneut.' : 'Die Schul- oder Personendaten sind derzeit nicht verfügbar. Bitte versuchen Sie es erneut.')}
            </p>
            <a class="btn btn-primary" href="/sus-schulen">Zur Schulauswahl</a
            ><button type="button" class="btn btn-outline" @click=${() => location.reload()}>Erneut laden</button>
          </div>
        </section>
      </main>
      ${PublicFooter()}
    </div>`;

  const section = (id: RegistrationSectionID, content: unknown) =>
    html`<div ?hidden=${activeSection.id !== id}>${content}</div>`;
  const next = async () => {
    if (activeIndex < sections.length - 1) {
      goToSection(sections[activeIndex + 1]!.id);
      return;
    }
    form.errors.value = [];
    setSubmitError('');
    setBusy(true);
    try {
      if (!(await form.validate())) setBusy(false);
    } catch (e) {
      setBusy(false);
      setSubmitError(e instanceof Error ? e.message : 'Die Angaben konnten nicht geprüft werden.');
    }
  };

  return html`<div id="sus-registration-page" class="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
    <main id="sus-registration-form" class="mx-auto flex w-full max-w-4xl flex-col gap-3 px-4 pt-4 sm:px-6 sm:pt-6">
      <header class="flex min-w-0 flex-col gap-3">
        <div class="flex min-w-0 flex-col items-start gap-2 md:flex-row md:items-center md:gap-6">
          <div
            class="flex shrink-0 items-center gap-1.5 md:gap-2"
            role="group"
            aria-label="Bewerbung · Schritt 3 von 3: Bewerbung ausfüllen"
          >
            <span
              class="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-content md:size-12 md:text-2xl"
              aria-hidden="true"
              >3</span
            >
            <div class="text-sm leading-snug">
              <p class="font-semibold text-primary">Bewerbung</p>
              <p>Schritt 3 von 3</p>
            </div>
          </div>
          <h1 class="w-full min-w-0 text-xl font-bold leading-snug tracking-tight sm:text-2xl md:w-auto lg:text-3xl">
            Bewerbung an der ${schoolData[0].name}
          </h1>
        </div>
        <nav class="card p-2 sm:px-6 sm:py-4" aria-label="Abschnitte der Bewerbung">
          <ol class="steps w-full [&_.step]:min-w-0" lang="de">
            ${sections.map(
              (item, index) => html`
                <li
                  class="step cursor-pointer ${errorSections.has(item.id) ? 'step-error' : index <= activeIndex ? 'step-primary' : ''}"
                  data-content=${errorSections.has(item.id) ? '!' : nothing}
                  @click=${() => !busy && goToSection(item.id)}
                >
                  <button
                    type="button"
                    class="sr-only sm:not-sr-only sm:min-w-0 sm:max-w-full sm:px-1 sm:text-center sm:text-sm sm:wrap-anywhere sm:hyphens-auto ${errorSections.has(item.id) ? 'text-error' : item.id === activeSection.id ? 'font-semibold text-primary' : ''}"
                    aria-current=${item.id === activeSection.id ? 'step' : nothing}
                    aria-invalid=${errorSections.has(item.id) ? 'true' : nothing}
                    ?disabled=${busy}
                  >
                    ${item.label}
                  </button>
                </li>
              `,
            )}
          </ol>
        </nav>
        <h2
          id="sus-section-heading"
          tabindex="-1"
          class="text-sm font-semibold text-base-content/70 outline-none sm:sr-only"
          aria-live="polite"
        >
          Abschnitt ${activeIndex + 1} von ${sections.length}: ${activeSection.label}
        </h2>
      </header>
      <fieldset class="flex min-w-0 flex-col gap-5" ?disabled=${busy}>
        ${section('sus-info', RegistrationInfoScreen({ schoolName: schoolData[0].name ?? '', klasse }))}
        ${section(
          'sus-child',
          html`<section class="card">
            <div class="card-body gap-5 p-4 sm:p-6">${SgSusEntryFormStudentFields({ form })}</div>
          </section>`,
        )}
        ${section('sus-guardians', SgSusEntryFormSbFields({ form, schoolID: userData.schoolID }))}
        ${section(
          'sus-school',
          html`<div class="flex min-w-0 flex-col gap-5">
            <section class="card w-full">
              <div class="card-body gap-5 p-4 sm:p-6">
                <h2 class="font-semibold md:text-lg">Klassenstufe</h2>
                <p>
                  Welche Angaben und Dokumente wir benötigen, hängt von der Klassenstufe ab. Wenn Sie sie ändern, passen
                  sich die weiteren Fragen und Abschnitte daran an.
                </p>
                <div class="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
                  ${RegistrationField(form, ['klassenstufe'], {
                    label: 'Gewünschte Klassenstufe',
                    required: true,
                    options: getPossibleKlassenForSchoolType(schoolData[0].schoolType, schoolData[0].mode).map(
                      klasse => ({ value: klasse, label: klasseLabel(klasse) }),
                    ),
                    onPicked: klasse => {
                      form.pickValueForField(['mode'], calculateRegisterMode(String(klasse), userData.origin));
                      // a changed class leaves or newly enters the transition from primary school, the only mode with an interview
                      form.pickValueForField(['selectedInterviewSlotID'], null);
                      setSelectedSlot(null);
                    },
                  })}
                </div>
              </div>
            </section>
            ${mode !== RegisterMode.REGISTRATION_NEW_ELEMENTARY ? SgSusEntryFormSchoolData({ form, school: schoolData[0], mode }) : nothing}
            ${SgSusEntryFormSchoolingInterests({ form, abstractSchoolType })}
          </div>`,
        )}
        ${section('sus-documents', SgSusEntryFormFiles({ form, mode }))}
        ${section(
          'sus-motivation-section',
          html`<section class="card">
            <div class="card-body gap-5 p-4 sm:p-6">
              <h2 class="font-semibold md:text-lg">Motivation</h2>
              ${RegistrationField(form, ['motivation'], { label: `Warum soll Ihr Kind die ${schoolData[0].name} besuchen?`, required: true, type: 'textarea' })}
              ${mode === RegisterMode.REGISTRATION_ELEMENTARY_ELEMENTARY || mode === RegisterMode.REGISTRATION_HIGH_HIGH ? RegistrationField(form, ['schulwechselMotivation'], { label: 'Warum streben Sie einen Schulwechsel an?', required: true, type: 'textarea' }) : nothing}
            </div>
          </section>`,
        )}
        ${mode === RegisterMode.REGISTRATION_ELEMENTARY_HIGH ? section('sus-interview', SGSusEntryFormInterviewSlots({ schoolID: userData.schoolID, form, onSelected: setSelectedSlot })) : nothing}
      </fieldset>
      <p class="text-sm text-base-content/70">Für die Klassenstufe ${klasse}. Felder mit * sind Pflichtfelder.</p>
      ${saveFailed ? html`<p class="text-sm text-error" role="alert">Ihr Browser kann den Entwurf nicht speichern. Bitte lassen Sie diese Seite geöffnet, bis Sie die Bewerbung abgesendet haben.</p>` : nothing}
      ${submitError ? html`<div class="alert alert-error alert-soft text-base-content" role="alert">${submitError}</div>` : nothing}
      <!-- a floating glass bar inside the column: lifted off the window edge, so the form scrolls visibly beneath it -->
      <div
        class="sticky bottom-3 z-10 mb-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-box border border-base-300 bg-base-100/80 p-2 shadow-lg backdrop-blur-md backdrop-saturate-150"
      >
        ${
          activeIndex === 0
            ? html`<a
                class="btn btn-outline min-h-11 justify-self-start gap-2"
                href="/sus-schulen"
                title="Zur Schulauswahl"
              >
                <span class="icon-[material-symbols--school-outline-rounded] text-xl" aria-hidden="true"></span>
                <span class="sr-only sm:not-sr-only">Zur Schulauswahl</span>
              </a>`
            : html`<button
                type="button"
                class="btn btn-outline min-h-11 justify-self-start"
                ?disabled=${busy}
                @click=${() => goToSection(sections[activeIndex - 1]!.id)}
              >
                Zurück
              </button>`
        }
        <p
          class="flex items-center gap-1 text-center text-sm text-base-content/70 ${savedAt ? '' : 'invisible'}"
          role="status"
          aria-live="polite"
        >
          <span class="icon-[material-symbols--check-circle-rounded] text-success" aria-hidden="true"></span>Automatisch
          gespeichert
        </p>
        <button type="button" class="btn btn-primary min-h-11 justify-self-end" ?disabled=${busy} @click=${next}>
          ${busy ? html`<span class="loading loading-spinner loading-sm" aria-hidden="true"></span>Bitte warten …` : activeIndex === sections.length - 1 ? 'Angaben prüfen' : 'Weiter'}
        </button>
      </div>
    </main>
    ${PublicFooter({ compactMobile: true })}
  </div>`;
});
